/**
 * Traducción de errores de la API de Clientes a la vista (ADR 0021).
 *
 * El backend responde con un contrato uniforme; `apiClient` ya lo normaliza a `ApiError`.
 * Acá se decide qué mostrar y a qué campo del formulario apuntar el error:
 * - Siempre se muestra el `message` del backend (fuente de verdad del texto).
 * - Si el error identifica el campo (`details.field` / `details.fieldName`), se marca ese.
 * - Si no, se cae al campo conocido por el `code` estable `AppErrorCode` (ADR 0021: sin
 *   parsear mensajes). El backend de clientes (US-2.3) ya publica sus códigos, así que se
 *   mapean acá (mismo patrón que `allyErrors.ts`/`locationErrors.ts`).
 */
import { ApiError } from '@/lib/http/apiClient';

export interface CustomerApiError {
  /** Mensaje del backend, listo para mostrar. */
  message: string;
  /** Nombre del campo del formulario a marcar, si el error lo identifica. */
  field?: string;
}

/**
 * Códigos estables del backend (`AppErrorCode`) → campo del formulario a marcar.
 *
 * - `CUSTOMER_EMAIL_DUPLICATED` → `email`.
 * - `CUSTOMER_PHONE_DUPLICATED` → `phone`: el formulario usa `phone` (el DTO del backend
 *   lo traduce a `telefono`; el `setFields` de la página necesita el nombre del Form.Item).
 * - `CUSTOMER_DOCUMENT_DUPLICATED` / `CUSTOMER_DOCUMENT_INVALID` → `documentNumber`.
 * - `CUSTOMER_BALANCE_AMOUNT_INVALID` → `amountCop` (modal de ajuste de saldo).
 * - `CUSTOMER_ALIADO_INVALID` → `aliadoId` (filtro del listado; el alta ya no envía
 *   aliado, la relación se deriva de la actividad — ADR 0049).
 * - `CUSTOMER_NOT_FOUND` no marca campo: es un 404 de la entidad, se muestra como mensaje.
 */
const CODE_FIELD: Record<string, string> = {
  CUSTOMER_EMAIL_DUPLICATED: 'email',
  CUSTOMER_PHONE_DUPLICATED: 'phone',
  CUSTOMER_DOCUMENT_DUPLICATED: 'documentNumber',
  CUSTOMER_DOCUMENT_INVALID: 'documentNumber',
  CUSTOMER_BALANCE_AMOUNT_INVALID: 'amountCop',
  CUSTOMER_ALIADO_INVALID: 'aliadoId',
};

/** Extrae el campo de `details` (convención ADR 0021) si viene. */
function fieldFromDetails(details: unknown): string | undefined {
  if (typeof details !== 'object' || details === null) {
    return undefined;
  }
  const record = details as Record<string, unknown>;
  for (const key of ['field', 'fieldName'] as const) {
    const value = record[key];
    if (typeof value === 'string' && value !== '') {
      return value;
    }
  }
  return undefined;
}

/**
 * Describe un error de cliente para mostrarlo en la página. `fallbackMessage` ajusta el
 * texto genérico según la acción (alta, actualización de estado, ajuste de saldo).
 */
export function describeCustomerApiError(
  error: unknown,
  fallbackMessage = 'No se pudo crear el cliente.',
): CustomerApiError {
  if (error instanceof ApiError) {
    return {
      message: error.message,
      field: fieldFromDetails(error.details) ?? CODE_FIELD[error.code],
    };
  }
  if (error instanceof Error && error.message) {
    return { message: error.message };
  }
  return { message: fallbackMessage };
}
